// ENVIRONMENT
import { environment } from 'src/environments/environment';

// ANGULAR
import { RouterModule, Routes } from '@angular/router';
import { NgModule } from '@angular/core';

// APP
import { AppLayoutComponent } from './app-layout.component';
import { WHContentPageComponent } from './w-h-content-page/w-h-content-page/w-h-content-page.component';

// AUTH0
import { AuthGuard } from '@auth0/auth0-angular';

// API
import {
  WHFeatureKeyENUM,
  WHPermissionKeyENUM,
  WHRouteZeroENUM,
  WHDiagnosticPageComponent,
  WHDiagnosticPageContentComponent,
  IWHPageConfigModel,
  WHRouteBreadcrumbData,
} from '@workheld/workheld-shared-lib';
import {
  layoutLoggedInUserNavItemDOMList,
  layoutNavItemDOMList,
} from './app.config';
import {
  bryntumSchedulerLibVersion,
  workheldSharedLibVersion,
  workheldWebClientVersion,
} from 'src/environments/env.config';
import { CheckUnsavedCanDeactivateGuard } from './app-shared/guards/check-unsaved.guard';
import { DevelopmentGuard } from './app-services-helper/dev-guard-helper.service';
import { teamPlannerV2Match } from './app-pages/w-h-team-planner-v2-page/team-planner-v2.routes';
import { TEAM_PLANNER_CLASSIC_PATH } from './app-pages/w-h-team-planner-v2-page/team-planner-v2.constants';
import {
  FeatureKeyGuard,
  NO_PERMISSIONS_ROUTE,
} from './app-services-helper/route-guard-helper.service';

// Breadcrumb of the work object -> work order page. The url starts with the
// work object type (projects / cases / standingorders), which decides the labels.
const WORK_OBJECT_TYPE_BREADCRUMB: Record<
  string,
  { label: string; descLabel: string }
> = {
  [WHRouteZeroENUM.PROJECTS]: {
    label: 'nav.work.project',
    descLabel: 'project.model.root',
  },
  [WHRouteZeroENUM.CASES]: {
    label: 'nav.work.case',
    descLabel: 'case.model.root',
  },
  [WHRouteZeroENUM.STANDING_ORDER]: {
    label: 'nav.work.standingorder',
    descLabel: 'standingorder.model.root',
  },
};
const WORK_OBJECT_WORK_ORDER_BREADCRUMB: WHRouteBreadcrumbData = [
  {
    label: (route) =>
      WORK_OBJECT_TYPE_BREADCRUMB[route.params['workObjectTypeId']]?.label,
  },
  { skip: true },
  {
    descLabel: (route) =>
      WORK_OBJECT_TYPE_BREADCRUMB[route.params['workObjectTypeId']]?.descLabel,
  },
  { descLabel: 'workorder.model.root' },
];

export const routes: Routes = [
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: `${WHRouteZeroENUM.HOME}`,
        data: { breadcrumb: { label: 'nav.overview.statusdashboard' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-status-overview/work-object-status-overview.module').then(
            (m) => m.WorkObjectStatusOverviewModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.PROJECT_OVERVIEW}`,
        data: { breadcrumb: { label: 'nav.overview.projectoverview' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/project-overview/project-overview.module').then(
            (m) => m.ProjectOverviewModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.TEAM_PLANNER}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.overview.teamplanner' },
          featureKey: WHFeatureKeyENUM.ADVANCED_TEAM_PLANNER,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_TEAM_PLANNER_READ,
        },
        children: [
          // Classic planner stays reachable while Team Planner 2.0 is piloted (WHP-12988)
          {
            path: TEAM_PLANNER_CLASSIC_PATH,
            loadChildren: () =>
              import('./app-pages/w-h-team-planner-page/w-h-team-planner-page.module').then(
                (m) => m.WHTeamPlannerPageModule,
              ),
          },
          {
            path: '',
            canMatch: [teamPlannerV2Match],
            loadChildren: () =>
              import('./app-pages/w-h-team-planner-v2-page/team-planner-v2.routes').then(
                (m) => m.TEAM_PLANNER_V2_ROUTES,
              ),
          },
          {
            path: '',
            loadChildren: () =>
              import('./app-pages/w-h-team-planner-page/w-h-team-planner-page.module').then(
                (m) => m.WHTeamPlannerPageModule,
              ),
          },
        ],
      },
      {
        path: `${WHRouteZeroENUM.ENTRIES_UNASSIGNED}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.overview.unassignedentries' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_UNASSIGNED_ITEMS,
        },
        loadChildren: () =>
          import('./app-modules-pages/entry/entry-unassigned/entry-unassigned.module').then(
            (m) => m.EntryUnassignedModule,
          ),
      },
      {
        path: WHRouteZeroENUM.MAINTENANCE_NOTIFICATIONS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'ui.maintenancenotifications.label' },
          featureKey: WHFeatureKeyENUM.MAINTENANCE_NOTIFICATIONS,
          permissionKeys: [
            WHPermissionKeyENUM.FLOW_MAINTENANCE_NOTIFICATION_READ,
            WHPermissionKeyENUM.FLOW_MAINTENANCE_NOTIFICATION_READ_OWN,
          ],
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/entry/entry-maintenance-notifications_new/entry-maintenance-notifications-table/entry-maintenance-notifications-table.component').then(
                (m) => m.EntryMaintenanceNotificationsTableComponent,
              ),
          },
        ],
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}`,
        data: { breadcrumb: { label: 'nav.overview.reportsoverview' } },
        component: WHContentPageComponent,
        children: [
          {
            path: '',
            loadChildren: () =>
              import('./app-pages/reports-page/reports-page.module').then(
                (m) => m.ReportsPageModule,
              ),
          },
          {
            path: `${WHRouteZeroENUM.WORK_OBJECT_EFFORT_REPORT}`,
            canActivate: [FeatureKeyGuard],
            data: {
              breadcrumb: { label: 'nav.overview.effortreport' },
              featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_EFFORT_REPORT,
            },
            loadChildren: () =>
              import('./app-pages/effort-report-page/effort-report-page.module').then(
                (m) => m.EffortReportPageModule,
              ),
          },
          {
            path: `${WHRouteZeroENUM.WORK_OBJECT_MATERIALUSAGE_REPORT}`,
            canActivate: [FeatureKeyGuard],
            data: {
              breadcrumb: { label: 'nav.overview.materialusagereport' },
              featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_MATERIALUSAGE_REPORT,
            },
            loadChildren: () =>
              import('./app-pages/material-usage-report-page/material-usage-report-page.module').then(
                (m) => m.MaterialUsageReportPageModule,
              ),
          },
          {
            path: `${WHRouteZeroENUM.WORK_OBJECT_COMPLETION_REPORT}`,
            canActivate: [FeatureKeyGuard],
            data: {
              breadcrumb: { label: 'nav.overview.workobjectcompletionreport' },
              featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_WORK_OBJECT_REPORT,
            },
            loadChildren: () =>
              import('./app-pages/work-object-completion-report-page/work-object-completion-report-page.module').then(
                (m) => m.WorkObjectCompletionReportPageModule,
              ),
          },
          {
            path: `${WHRouteZeroENUM.TIME_REPORT}`,
            canActivate: [FeatureKeyGuard],
            data: {
              breadcrumb: { label: 'nav.humanresource.weeklytimereport' },
              featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_WEEKLY_REPORT,
            },
            loadChildren: () =>
              import('./app-modules-pages/worker/worker-time-report/worker-time-report.module').then(
                (m) => m.WorkerTimeReportModule,
              ),
          },
          {
            path: `${WHRouteZeroENUM.EQUIPMENT_WORK_OBJECT_REPORT}`,
            data: { breadcrumb: { label: 'report.equipmentworkobjectreport.title' } },
            loadComponent: () =>
              import(
                './app-pages/equipment-work-object-report-page/equipment-work-object-report-page.component'
              ).then((m) => m.EquipmentWorkObjectReportPageComponent),
          },
        ],
      },
      {
        path: `${WHRouteZeroENUM.PROJECTS}/:customerId/:workObjectId`,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.work.project' }, { skip: true }, { descLabel: 'project.model.root' } ],
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_PROJECT,
          permissionKey: WHPermissionKeyENUM.FLOW_TECHNICIAN_PROJECT_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/project/project-crud/project-crud.module').then(
            (m) => m.ProjectCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.PROJECTS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.work.project' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_PROJECT,
          permissionKey: WHPermissionKeyENUM.FLOW_TECHNICIAN_PROJECT_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-overview/work-object-overview.module').then(
            (m) => m.WorkObjectOverviewModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CASES}/:customerId/:workObjectId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.work.case' }, { skip: true }, { descLabel: 'case.model.root' } ],
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_CASE,
          permissionKey: WHPermissionKeyENUM.FLOW_TECHNICIAN_CASE_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/case/case-crud/case-crud.module').then(
            (m) => m.CaseCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CASES}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.work.case' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_CASE,
          permissionKey: WHPermissionKeyENUM.FLOW_TECHNICIAN_CASE_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-overview/work-object-overview.module').then(
            (m) => m.WorkObjectOverviewModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CASES_CATEGORY}`,
        data: { breadcrumb: { label: 'nav.work.casecreationwidget' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/case/case-quick-create/case-quick-create.module').then(
            (m) => m.CaseQuickCreateModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.STANDING_ORDER}/:customerId/:workObjectId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.work.standingorder' }, { skip: true }, { descLabel: 'standingorder.model.root' } ],
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_STANDING_ORDER,
          permissionKey:
            WHPermissionKeyENUM.FLOW_TECHNICIAN_STANDING_ORDER_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/standing-order/standing-order-crud/standing-order-crud.module').then(
            (m) => m.StandingOrderCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.STANDING_ORDER}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.work.standingorder' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW_STANDING_ORDER,
          permissionKey:
            WHPermissionKeyENUM.FLOW_TECHNICIAN_STANDING_ORDER_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-overview/work-object-overview.module').then(
            (m) => m.WorkObjectOverviewModule,
          ),
      },
      {
        path: `:workObjectTypeId/:customerId/:workObjectId/:workOrderId`,
        data: { breadcrumb: WORK_OBJECT_WORK_ORDER_BREADCRUMB },
        component: WHContentPageComponent,
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/work-order/work-order-crud/work-order-crud.module').then(
            (m) => m.WorkOrderCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.EQUIPMENTS}/:nodeId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [
            { label: 'nav.equipment.management' },
            // 'add' also matches :nodeId; detail labels are set by EquipmentComponent
            { label: (route) => (route.params['nodeId'] === 'add' ? 'equipment.ui.create.label' : undefined) },
          ],
          permissionKey: WHPermissionKeyENUM.FLOW_EQUIPMENT_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/equipment/stores/equipment.component').then(
                (m) => m.EquipmentComponent,
              ),
          },
        ],
      },
      {
        path: `${WHRouteZeroENUM.EQUIPMENTS}/add`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.equipment.management' }, { skip: true } ],
          permissionKey: WHPermissionKeyENUM.FLOW_EQUIPMENT_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/equipment/stores/equipment.component').then(
                (m) => m.EquipmentComponent,
              ),
          },
        ],
      },
      {
        path: WHRouteZeroENUM.QUALIFICATIONS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.qualifications.root' },
          featureKey: WHFeatureKeyENUM.SKILL_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.FLOW_QUALIFICATION_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/qualifications/qualifications.module').then(
            (m) => m.QualificationsModule,
          ),
      },
      {
        path: WHRouteZeroENUM.JOB_REQUIREMENTS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.jobrequirements.root' },
          featureKey: WHFeatureKeyENUM.SKILL_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.FLOW_JOB_PROFILE_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/job-requirements/job-requirements.module').then(
            (m) => m.JobRequirementsModule,
          ),
      },
      {
        path: WHRouteZeroENUM.EXECUTION_GROUPS,
        data: { breadcrumb: { label: 'nav.masterdata.executiongroups' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/execution-groups/execution-groups.module').then(
            (m) => m.ExecutionGroupsModule,
          ),
      },
      {
        path: WHRouteZeroENUM.SHIFT_DEFINITIONS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.shiftdefinition.root' },
          featureKey: WHFeatureKeyENUM.SHIFT_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.FLOW_TEAM_SHIFT_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/shift-definitions/shift-definitions.module').then(
            (m) => m.ShiftDefinitionsModule,
          ),
      },
      {
        path: WHRouteZeroENUM.SHIFT_PLANNER,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.overview.shiftplanner' },
          featureKey: WHFeatureKeyENUM.SHIFT_PLANNER,
          permissionKey: WHPermissionKeyENUM.FLOW_TEAM_SHIFT_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/shift-planner/shift-planner.module').then(
            (m) => m.ShiftPlannerModule,
          ),
      },
      {
        path: WHRouteZeroENUM.SHIFT_BOOK,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.overview.shiftbook' },
          featureKey: WHFeatureKeyENUM.SHIFT_BOOK,
          permissionKey: WHPermissionKeyENUM.FLOW_SHIFT_BOOK_READ,
        },
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/shiftbook/shift-book-records/shift-book-records.component').then(
                (m) => m.ShiftBookRecordsComponent,
              ),
          },
          {
            path: 'add',
            data: { breadcrumb: { descLabel: 'app.ui.shiftbookrecord.label' } },
            loadComponent: () =>
              import('./app-modules-pages/shiftbook/shift-book-record/shift-book-add.component').then(
                (m) => m.ShiftBookAddComponent,
              ),
          },
          {
            path: ':id',
            data: { breadcrumb: { descLabel: 'app.ui.shiftbookrecord.label' } },
            loadComponent: () =>
              import('./app-modules-pages/shiftbook/shift-book-record/shift-book-record.component').then(
                (m) => m.ShiftBookRecordComponent,
              ),
          },
        ],
      },
      {
        path: WHRouteZeroENUM.DAILY_DASHBOARD,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.overview.dailydashboard' },
          featureKey: WHFeatureKeyENUM.DAILY_DASHBOARD,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_DAILY_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/daily-dashboard/daily-dashboard.module').then(
            (m) => m.DailyDashboardModule,
          ),
      },
      {
        path: WHRouteZeroENUM.INFOSCREEN,
        data: { breadcrumb: { label: 'nav.overview.infoscreen' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-modules-pages/infoscreen/infoscreen.module').then(
            (m) => m.InfoScreenModule,
          ),
      },
      {
        path: WHRouteZeroENUM.EQUIPMENTS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.equipment.management' },
          featureKey: WHFeatureKeyENUM.INSTALLED_BASE,
          permissionKey: WHPermissionKeyENUM.FLOW_EQUIPMENT_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/equipment/equipment-data-table/equipment-data-table.module').then(
            (m) => m.EquipmentDataTableModule,
          ),
      },
      {
        path: WHRouteZeroENUM.EQUIPMENT_PLANNER,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.equipment.planner' },
          featureKey: WHFeatureKeyENUM.EQUIPMENT_PLANNER,
          permissionKey: WHPermissionKeyENUM.FLOW_EQUIPMENT_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/equipment/equipment-planner/equipment-planner.module').then(
            (m) => m.EquipmentPlannerModule,
          ),
      },
      {
        path: 'eqgroups',
        component: WHContentPageComponent,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/equipment/groups/equipment-planner-groups.component').then(
                (m) => m.EquipmentPlannerGroupsComponent,
              ),
          },
        ],
      },
      {
        path: WHRouteZeroENUM.SKILLS,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.skills.root' },
          featureKey: WHFeatureKeyENUM.SKILL_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.FLOW_SKILL_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/skills/skills-data-table/skills-data-table.module').then(
            (m) => m.SkillsDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.SKILLS}/:skillId`,
        data: { breadcrumb: [ { label: 'nav.skills.root' }, { descLabel: 'app.ui.skill.label' } ] },
        component: WHContentPageComponent,
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/skills/skills-crud/skills-crud.module').then(
            (m) => m.SkillsCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.HR_WORKERS}/:hrWorkerId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.hrworker.root' }, { descLabel: 'humanresource.model.root' } ],
          featureKey: WHFeatureKeyENUM.HUMAN_RESOURCES,
          permissionKey: WHPermissionKeyENUM.FLOW_HR_WORKER_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/hr-worker/hr-worker-crud/hr-worker-crud.module').then(
            (m) => m.HrWorkerCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.HR_WORKERS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.hrworker.root' },
          featureKey: WHFeatureKeyENUM.HUMAN_RESOURCES,
          permissionKey: WHPermissionKeyENUM.FLOW_HR_WORKER_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/hr-worker/hr-worker-data-table/hr-worker-data-table.module').then(
            (m) => m.HrWorkerDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.SHIFT_GROUPS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.shiftgroup.root' },
          featureKey: WHFeatureKeyENUM.SHIFT_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.FLOW_WORK_TIME_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/shift-group/shift-group-data-table/shift-group-data-table.module').then(
            (m) => m.ShiftGroupDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.SHIFT_CALENDAR}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.shiftcalendar.root' },
          featureKey: WHFeatureKeyENUM.SHIFT_MANAGEMENT,
          permissionKey:
            WHPermissionKeyENUM.FLOW_SHIFT_CALENDAR_DEFINITION_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/shift-calendar/shift-calendar-data-table/shift-calendar-data-table.module').then(
            (m) => m.ShiftCalendarDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.SHIFT_CALENDAR}/:shiftCalendarId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.shiftcalendar.root' }, { descLabel: 'nav.shiftcalendar.label' } ],
          featureKey: WHFeatureKeyENUM.SHIFT_MANAGEMENT,
          permissionKey:
            WHPermissionKeyENUM.FLOW_SHIFT_CALENDAR_DEFINITION_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/shift-calendar/shift-calendar-crud/shift-calendar-crud.module').then(
            (m) => m.ShiftCalendarCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.ABSENCE_REQUESTS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.absencerequest.root' },
          featureKey: WHFeatureKeyENUM.ABSENCE_REQUESTS,
          permissionKey: WHPermissionKeyENUM.FLOW_ABSENCE_REQUEST_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/absencerequest/absencerequest-data-table/absencerequest-data-table.module').then(
            (m) => m.AbsencerequestDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.ABSENCE_REQUESTS}/:absenceRequestId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.absencerequest.root' }, { descLabel: 'nav.absencerequest.label' } ],
          featureKey: WHFeatureKeyENUM.ABSENCE_REQUESTS,
          permissionKey: WHPermissionKeyENUM.FLOW_ABSENCE_REQUEST_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/absencerequest/absencerequest-details/absencerequest-details.module').then(
            (m) => m.AbsencerequestDetailsModule,
          ),
      },
      {
        path: WHRouteZeroENUM.MAINTENANCE_PLAN_FIX,
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-pages/w-h-maintenance-plan-page-fix/w-h-maintenance-plan-page-fix.module').then(
            (m) => m.WHMaintenancePlanPageFixModule,
          ),
      },
      {
        path: WHRouteZeroENUM.MAINTENANCE_PLAN,
        data: { breadcrumb: { label: 'nav.equipment.maintenanceplan' } },
        component: WHContentPageComponent,
        loadChildren: () =>
          import('./app-pages/w-h-maintenance-plan-page/w-h-maintenance-plan-page.module').then(
            (m) => m.WHMaintenancePlanPageModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.MATERIALS}/:materialId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.material.management' }, { descLabel: 'material.model.root' } ],
          featureKey: WHFeatureKeyENUM.MATERIAL,
          permissionKey: WHPermissionKeyENUM.FLOW_MATERIAL_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/material/material-crud/material-crud.module').then(
            (m) => m.MaterialCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.MATERIALS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.material.management' },
          featureKey: WHFeatureKeyENUM.MATERIAL,
          permissionKey: WHPermissionKeyENUM.FLOW_MATERIAL_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/material/material-data-table/material-data-table.module').then(
            (m) => m.MaterialDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.MATERIALINVENTORY}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.humanresource.carstock' },
          featureKey: WHFeatureKeyENUM.MATERIAL_INVENTORY,
          permissionKey: WHPermissionKeyENUM.FLOW_MATERIAL_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/material/material-inventory-overview/material-inventory-overview.module').then(
            (m) => m.MaterialInventoryOverviewModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CUSTOMERS}/:customerId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.masterdata.customer' }, { descLabel: 'customer.model.root' } ],
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_CUSTOMER_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/customer/customer-crud/customer-crud.module').then(
            (m) => m.CustomerCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CUSTOMERS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.masterdata.customer' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_CUSTOMER_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/customer/customer-master-data-table/customer-master-data-table.module').then(
            (m) => m.CustomerMasterDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.CHECKLIST_DEFINITION}/:checklistDefinitionId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [
            { label: 'nav.masterdata.checklistdefinition' },
            { descLabel: 'checklistdefinition.model.root' },
          ],
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW,
          permissionKey:
            WHPermissionKeyENUM.FLOW_TECHNICIAN_CHECKLIST_TEMPLATE_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/checklist-definition/checklist-definition-crud/checklist-definition-crud.module').then(
            (m) => m.ChecklistDefinitionCrudModule,
          ),
        // data: {
        //   apiUrl: environment.apiUrl
        // }
      },
      {
        path: `${WHRouteZeroENUM.CHECKLIST_DEFINITION}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.masterdata.checklistdefinition' },
          featureKey: WHFeatureKeyENUM.CORE_WORK_FLOW,
          permissionKey:
            WHPermissionKeyENUM.FLOW_TECHNICIAN_CHECKLIST_TEMPLATE_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/checklist-definition/checklist-definition-master-data-table/checklist-definition-master-data-table.module').then(
            (m) => m.ChecklistDefinitionMasterDataTableModule,
          ),
        // data: {
        //   apiUrl: environment.apiUrl
        // }
      },
      {
        path: `${WHRouteZeroENUM.TOOLS}/:toolId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.masterdata.tool' }, { descLabel: 'tool.model.root' } ],
          featureKey: WHFeatureKeyENUM.TOOL,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_TOOL_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/tool/tool-crud/tool-crud.module').then(
            (m) => m.ToolCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.TOOLS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.masterdata.tool' },
          featureKey: WHFeatureKeyENUM.TOOL,
          permissionKey: WHPermissionKeyENUM.FLOW_COORDINATOR_TOOL_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/tool/tool-data-table/tool-data-table.module').then(
            (m) => m.ToolDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_TEMPLATES}/:workObjectTypeId/:templateId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.masterdata.template' }, { skip: true }, { descLabel: 'template.model.root' } ],
          featureKey: WHFeatureKeyENUM.TEMPLATE,
          permissionKey:
            WHPermissionKeyENUM.FLOW_COORDINATOR_PROJECT_TEMPLATE_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-template-crud/work-object-template-crud.module').then(
            (m) => m.WorkObjectTemplateCrudModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_TEMPLATES}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.masterdata.template' },
          featureKey: WHFeatureKeyENUM.TEMPLATE,
          permissionKey:
            WHPermissionKeyENUM.FLOW_COORDINATOR_PROJECT_TEMPLATE_READ,
        },
        loadChildren: () =>
          import('./app-modules-pages/work-object/work-object-template-data-table/work-object-template-data-table.module').then(
            (m) => m.WorkObjectTemplateDataTableModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.WORKERS}/:workerId`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: [ { label: 'nav.masterdata.technicianonboarding' }, { descLabel: 'worker.model.root' } ],
          featureKey: WHFeatureKeyENUM.TECHNICIAN_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.MANAGEMENT_USER_READ,
        },
        canDeactivate: [CheckUnsavedCanDeactivateGuard],
        loadChildren: () =>
          import('./app-pages/worker/worker-crud-page/worker-crud-page.module').then(
            (m) => m.WorkerCrudPageModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.WORKERS}`,
        component: WHContentPageComponent,
        canActivate: [FeatureKeyGuard],
        data: {
          breadcrumb: { label: 'nav.masterdata.technicianonboarding' },
          featureKey: WHFeatureKeyENUM.TECHNICIAN_MANAGEMENT,
          permissionKey: WHPermissionKeyENUM.MANAGEMENT_USER_READ,
        },
        loadChildren: () =>
          import('./app-pages/worker/worker-management-page/worker-management-page.module').then(
            (m) => m.WorkerManagementPageModule,
          ),
      },
      {
        path: `${WHRouteZeroENUM.PROFILE}`,
        component: WHContentPageComponent,
        loadChildren: () =>
          import('@workheld/workheld-shared-lib').then(
            (m) => m.WHProfilePageModule,
          ),
        data: {
          breadcrumb: { label: 'nav.userprofile.root' },
          apiUrl: environment.apiUrl,
          pageConfig: {
            headerType: 'flow',
          } as IWHPageConfigModel,
        },
      },
      {
        path: `${WHRouteZeroENUM.DIAGNOSTICS}`,
        component: WHContentPageComponent,
        data: {
          breadcrumb: { label: 'nav.diagnostics.root' },
          pageConfig: {
            headerType: 'flow',
          } as IWHPageConfigModel,
        },
        children: [
          {
            path: '',
            canActivate: [DevelopmentGuard],
            component: WHDiagnosticPageComponent,
            data: {
              appVersion: workheldWebClientVersion,
              workheldSharedLibVersion,
              bryntumSchedulerLibVersion,
            },
            children: [
              {
                path: 'featureflag',
                component: WHDiagnosticPageContentComponent,
                loadChildren: () =>
                  import('@workheld/workheld-shared-lib').then(
                    (m) => m.WHFeatureFlagPageModule,
                  ),
                data: {
                  apiUrl: environment.apiUrl,
                  devFeatureConfigMap: environment.devFeatureConfigMap,
                  layoutNavItemDOMList,
                  layoutLoggedInUserNavItemDOMList,
                },
              },
              {
                path: 'i18n',
                component: WHDiagnosticPageContentComponent,
                loadChildren: () =>
                  import('@workheld/workheld-shared-lib').then(
                    (m) => m.WHI18nPageModule,
                  ),
                data: {
                  apiUrl: environment.apiUrl,
                },
              },
              {
                path: 'permissions',
                component: WHDiagnosticPageContentComponent,
                loadChildren: () =>
                  import('@workheld/workheld-shared-lib').then(
                    (m) => m.WHPermissionPageModule,
                  ),
              },
              // WHThemePageModule removed from @workheld/workheld-shared-lib v10.0.0
              // {
              //   path: 'theme',
              //   component: WHDiagnosticPageContentComponent,
              //   loadChildren: () =>
              //     import('@workheld/workheld-shared-lib').then(
              //       (m) => m.WHThemePageModule,
              //     ),
              // },
              {
                path: 'layout',
                component: WHDiagnosticPageContentComponent,
                loadChildren: () =>
                  import('@workheld/workheld-shared-lib').then(
                    (m) => m.WHLayoutSettingsPageModule,
                  ),
              },
              {
                path: 'bryntum',
                loadComponent: () =>
                  import('./app-modules-pages/bryntum-smoke/bryntum-smoke.component').then(
                    (m) => m.BryntumSmokeComponent,
                  ),
              },
              {
                path: 'planner-core',
                loadComponent: () =>
                  import('./app-modules-pages/planner-core/testing/planner-core-harness.component').then(
                    (m) => m.PlannerCoreHarnessComponent,
                  ),
              },
              {
                path: '',
                component: WHDiagnosticPageContentComponent,
              },
            ],
          },
        ],
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_EFFORT_REPORT}`,
        redirectTo: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}/${WHRouteZeroENUM.WORK_OBJECT_EFFORT_REPORT}`,
        pathMatch: 'full',
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_MATERIALUSAGE_REPORT}`,
        redirectTo: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}/${WHRouteZeroENUM.WORK_OBJECT_MATERIALUSAGE_REPORT}`,
        pathMatch: 'full',
      },
      {
        path: `${WHRouteZeroENUM.WORK_OBJECT_COMPLETION_REPORT}`,
        redirectTo: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}/${WHRouteZeroENUM.WORK_OBJECT_COMPLETION_REPORT}`,
        pathMatch: 'full',
      },
      {
        path: `${WHRouteZeroENUM.TIME_REPORT}`,
        redirectTo: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}/${WHRouteZeroENUM.TIME_REPORT}`,
        pathMatch: 'full',
      },
      {
        path: `${WHRouteZeroENUM.EQUIPMENT_WORK_OBJECT_REPORT}`,
        redirectTo: `${WHRouteZeroENUM.WORK_OBJECT_REPORTS_OVERVIEW}/${WHRouteZeroENUM.EQUIPMENT_WORK_OBJECT_REPORT}`,
        pathMatch: 'full',
      },
      {
        path: NO_PERMISSIONS_ROUTE,
        component: WHContentPageComponent,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./app-modules-pages/no-permissions/no-permissions.component').then(
                (m) => m.NoPermissionsComponent,
              ),
          },
        ],
      },
      {
        // All other routes redirect to the home page
        path: '**',
        redirectTo: `${WHRouteZeroENUM.HOME}`,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
